import {
  useEffect, useRef,
} from 'react'

/**
 * Poll `onPoll` while `enabled`, doubling the gap from `minMs` up to `maxMs`.
 *
 * Two things this exists to get right, both of which we got wrong by hand:
 *
 * 1. **Pace against the data, not the clock.** A flat interval sized for "the
 *    thing might happen any second now" keeps asking at that rate for the whole
 *    wait, long after the answer can plausibly have changed. That is what
 *    exhausted `/referral/network`'s hourly budget ~12 minutes in and froze the
 *    page the poll existed to keep fresh. Start responsive, then settle to the
 *    rate the underlying state actually moves at.
 * 2. **Survive the caller's re-renders.** `onPoll` is almost always an inline
 *    arrow or a `useCallback` over changing state, so depending on its identity
 *    tears the timer down and restarts its countdown on every parent render —
 *    and a parent that re-renders faster than the interval starves the poll
 *    completely. Holding it in a ref keeps the schedule out of that blast
 *    radius; the timer restarts only when `enabled` or the cadence changes.
 *
 * The backoff is deliberately monotonic — it does NOT reset when a poll returns
 * something. These waits are for a single transition (a scan run commits, IT
 * adds a DNS record), so resetting on activity just re-climbs the same wasted
 * requests. Pair this with an instant manual action in the UI so nobody has to
 * wait out a backed-off gap.
 *
 * `onPoll` rejecting is treated as a skipped tick, never as a reason to stop:
 * the rejection is swallowed here so it can neither kill the schedule nor
 * surface as an unhandled rejection. Callers wanting to react to a failure
 * should catch inside `onPoll`.
 */
export function usePollWithBackoff(
  { enabled, onPoll, minMs, maxMs }: {
    enabled: boolean
    onPoll: () => Promise<unknown>
    minMs: number
    maxMs: number
  },
): void {
  const onPollRef = useRef(onPoll)
  useEffect(() => {
    onPollRef.current = onPoll
  }, [onPoll])

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    let delay = minMs
    let timer = 0
    const tick = () => {
      void onPollRef.current()
        .catch(() => {})
        .finally(() => {
          if (cancelled) return
          delay = Math.min(delay * 2, maxMs)
          timer = window.setTimeout(tick, delay)
        })
    }
    timer = window.setTimeout(tick, delay)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [enabled, minMs, maxMs])
}

// Waiting on DNS delegation means waiting on another company's IT team to make
// a manual change: it can land in a minute or sit for days. Shared by both
// surfaces that watch for it — the executive's settings panel and the public
// page their IT team opens — so neither can drift into hammering the endpoint
// the other one's cap was sized against.
export const DNS_VERIFY_POLL_MIN_MS = 10_000
export const DNS_VERIFY_POLL_MAX_MS = 5 * 60_000
